import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import { TextButtonCell } from '~/components/common';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';
import { formatReceiveExpectedDateTime } from '~/utils/receive';

import { RECEIVE_REPORT_STATUS, RECEIVE_REPORT_STATUS_BADGE_COLOR } from '~/constants';
import { DATE_FORMAT } from '~/constants/common';

import type { TypeReceiveReportHistory } from '~/types/receive.types';

/**
 * [입고관리 > 입고 리포트 관리] 페이지 테이블
 */
const getReceiveReportListColumnDefs = ({
  onClickViewDetail,
}: {
  onClickViewDetail: (data: TypeReceiveReportHistory) => void;
}) => {
  const columnDefs: ColDef<TypeReceiveReportHistory>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
    },
    {
      field: 'reportCode',
      headerName: '리포트 ID',
      width: 180,
    },
    {
      field: 'requestCode',
      headerName: '입고요청번호',
      width: 180,
    },
    {
      field: 'reportStatus',
      headerName: '리포트 상태',
      width: 127,
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<TypeReceiveReportHistory, TypeReceiveReportHistory['reportStatus']>) => ({
        variant: 'light',
        status: true,
        color: value ? RECEIVE_REPORT_STATUS_BADGE_COLOR[value] : undefined,
        children: value ? RECEIVE_REPORT_STATUS[value].text : '-',
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'releaseName',
      headerName: '입고 방법',
      width: 85,
    },
    {
      field: 'sku',
      headerName: '리포트 SKU',
      width: 100,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveReportHistory, TypeReceiveReportHistory['sku']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'unit',
      headerName: '리포트 Unit',
      width: 100,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveReportHistory, TypeReceiveReportHistory['unit']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'taskNames',
      headerName: '작업 유형',
      width: 200,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeReceiveReportHistory, TypeReceiveReportHistory['taskNames']>) =>
        value ? value.join(', ') : '-',
    },
    {
      headerName: '',
      width: 80,
      cellRenderer: TextButtonCell,
      cellRendererParams: {
        children: '상세보기',
        showDashIfNoValue: false,
        onClick: ({ data }: ICellRendererParams<TypeReceiveReportHistory>) => {
          if (!data) return;
          onClickViewDetail(data);
        },
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      headerName: '입고예정일시',
      width: 200,
      valueFormatter: ({ data }: ValueFormatterParams<TypeReceiveReportHistory>) => {
        if (!data) {
          return '-';
        }
        return formatReceiveExpectedDateTime(data.receivingExpectedDate, data.receivingExpectedTime);
      },
    },
    {
      field: 'receivedDateTime',
      headerName: '입고확정일시',
      width: 160,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeReceiveReportHistory, TypeReceiveReportHistory['receivedDateTime']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'createdAt',
      headerName: '리포트 발행일시',
      width: 160,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeReceiveReportHistory, TypeReceiveReportHistory['createdAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'statusUpdatedAt',
      headerName: '리포트 상태변경일시',
      width: 160,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeReceiveReportHistory, TypeReceiveReportHistory['statusUpdatedAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getReceiveReportListColumnDefs;
